// Documentation of registration scenarios handling
//  This login screen supports 4 types of authentication:
//  Type 1 Email + Password
//  - User enters a valid email and password, its loginState.mode is "PASSWORD" and usedMethod is "email"
//  Type 2 Mobile + Password
//  - User enters a valid 10-digit mobile number and password, it's loginState.mode is "PASSWORD" and usedMethod is "mobile"
//  - API login is triggered directly with credentials
//  Type 3 Email + OTP
//  - User enters a valid email, it's loginState.mode is "OTP" and usedMethod is "email"
//  - OTP is sent to the email and then User verifies OTP on OTP screen
//  Type 4 Mobile + OTP
//  - User enters a valid 10-digit mobile number, it's loginState.mode is "OTP" amd usedMethod is "mobile"
//  - OTP is sent to the mobile number and then User verifies OTP on OTP screen

//  - `identifier` decides whether the user is logging in via email or mobile
//  - `getLoginType()` maps the combination to a backend login type code:
//     1 → EMAIL_PASSWORD
//     2 → MOBILE_PASSWORD
//     3 → EMAIL_OTP
//     4 → MOBILE_OTP
//  - PASSWORD mode:
//     Calls login API directly with credentials
//  - OTP mode:
//     Sends OTP, Shows OTP screen, Verifies OTP before allowing login

import React, { useState, useRef, useEffect, useContext } from "react";
import classnames from "classnames";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { REGEX, LOGIN_TYPE, OTP_TIMER } from "constants/CommonConstants";
import { login, resendOTP } from "action/auth";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import OTPScreen from "./OTPScreen";
import { isEmail, isMobile } from "components/CommonComponents/CommonFunctions";
import { useAuth } from "./AuthContext";
import { ROLE_BASED_DEFAULT_ROUTE } from "constants/CommonConstants";
import { encodePassword } from "components/CommonComponents/encodeDecodeFunction";

export default function Login() {
  const inputsRef = useRef([]);
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { loginWithPassword, verifyOtpLogin } = useAuth();

  const [loginInfo, setLoginInfo] = useState({
    identifier: { value: "", error: "" },
    password: { value: "", error: "" },
  });

  const [loginState, setLoginState] = useState({
    mode: "PASSWORD",
    usedMethod: null,
    otherData: null,
  });
  const [otpState, setOtpState] = useState({
    isOtpMode: false,
    otp: ["", "", "", "", "", ""],
    timer: OTP_TIMER,
    error: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showLoader, setShowLoader] = useState({
    getOtp: false,
    verifyOtp: false,
  });

  useEffect(() => {
    if (!otpState.isOtpMode) return;
    if (otpState.timer === 0) {
      //Shows expiry warning when OTP expires
      notify(
        "warning",
        "Your OTP has expired. Please resend the OTP to continue.",
      );
      return;
    }

    const interval = setInterval(
      () => setOtpState((prev) => ({ ...prev, timer: prev.timer - 1 })),
      500,
    );
    return () => clearInterval(interval);
  }, [otpState.timer, otpState.isOtpMode]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const error = searchParams.get("error");
    if (error) {
      notify("danger", error);
    }
    searchParams.delete("error");
    window.history.replaceState(null, "", `${window.location.pathname}`);
  }, []);

  const getLoginType = () => {
    if (isEmail(loginInfo.identifier.value))
      return loginState.mode === "PASSWORD" ? 1 : 3; // EMAIL_PASSWORD / EMAIL_OTP
    if (isMobile(loginInfo.identifier.value))
      return loginState.mode === "PASSWORD" ? 2 : 4; // MOBILE_PASSWORD / MOBILE_OTP
    return null;
  };

  const handleChange = (name, value) => {
    const trimmedValue = value.trim();
    setLoginInfo((prev) => ({
      ...prev,
      [name]: { ...prev[name], value: trimmedValue, error: "" },
    }));

    if (name === "identifier") {
      const usedMethod = isEmail(trimmedValue)
        ? "email"
        : isMobile(trimmedValue) || REGEX.OTP_STRING_REGEX.test(value) // check if it's mobile number or string of Digits (numbers) using regex
          ? "mobile"
          : null;
      setLoginState((prev) => ({
        ...prev,
        usedMethod,
      }));
    }
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "identifier": {
        if (!value) {
          return "Email or mobile number is required";
        } else if (REGEX.OTP_STRING_REGEX.test(value)) {
          // if enterd string is of Digits (numbers) then consider it as mobile number
          return isMobile(value)
            ? ""
            : "Please enter a valid 10-digit mobile number";
        } else {
          return isEmail(value) ? "" : "Please enter a valid email address";
        }
      }

      case "password": {
        if (loginState.mode === "PASSWORD" && !value)
          return "Password is required";
      }

      default:
        return "";
    }
  };

  const validateField = (name, value) => {
    const trimmed = value.trim();
    const error = getValidationError(name, trimmed);

    setLoginInfo((prev) => ({
      ...prev,
      [name]: { value: trimmed, error },
    }));
  };

  const validateForm = () => {
    let valid = true;
    let updated = { ...loginInfo };

    Object.keys(updated).forEach((key) => {
      if (key === "password" && loginState.mode === "OTP") return;

      const val = updated[key].value.trim();
      const error = getValidationError(key, val);
      updated[key].error = error;
      if (error) valid = false;
    });

    if (!getLoginType()) valid = false;

    setLoginInfo(updated);
    return valid;
  };

  const preventSpace = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  const handleBlur = (name, value) => {
    validateField(name, value);
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // important to stop form submit
      if (validateForm()) {
        handleGetOtp();
      }
    }
  };

  const handleGetOtp = async () => {
    if (!validateForm()) return;

    const loginTypeKey = getLoginType();
    if (!loginTypeKey) return;

    const loginTypeValue = LOGIN_TYPE[loginTypeKey];

    const loginData = {
      [loginState.usedMethod]: loginInfo.identifier.value,
      loginType: loginTypeValue,
      ...(loginState.mode === "PASSWORD" && {
        password: encodePassword(loginInfo.password.value),
      }),
    };

    setShowLoader((prev) => ({ ...prev, getOtp: true }));
    // remove this when actual api is integrated
    // navigate("/admin/dashboard");

    try {
      const res = await login(loginData);
      if (res.code === 200) {
        // // If it's password then redirect it to dashboard
        if (loginState.mode === "PASSWORD") {
          // AuthContext will save user + token
          const result = await loginWithPassword(loginData);

          if (result.success) {
            const role = result.data?.role; // CHANNEL_PARTNER //INSTRUCTOR //AADMIN

            const defaultRoute = ROLE_BASED_DEFAULT_ROUTE[role] || "/";
            notify("success", "Login successful");
            navigate(
              `${defaultRoute}?isPasswordSet=${res?.data?.isPasswordSet === "NO"}`,
              { replace: true },
            );
          } else {
            notify("danger", result.message);
          }
        } else if (loginState.mode === "OTP") {
          // // If it's OTP then redirect it to OTP verfiy screen
          setLoginState((prev) => ({ ...prev, otherData: res.data }));
          notify("success", res.message || "OTP sent successfully");
          setOtpState((prev) => ({
            ...prev,
            isOtpMode: true,
            timer: OTP_TIMER,
          }));

          // this is temp code to auto fill mobile otp received from backend
          if (loginState?.usedMethod === "mobile" && res?.data?.mobileOTP) {
            if (!REGEX.OTP_STRING_REGEX.test(res?.data?.mobileOTP)) return;

            const digits = res?.data?.mobileOTP.slice(0, 6).split("");

            setOtpState((prev) => ({
              ...prev,
              otp: digits,
              error: "",
            }));

            inputsRef.current[5]?.focus();

            if (digits.length === 6) {
              setTimeout(() => {
                handleVerifyOtp(digits, res.data);
              }, 3000);
            }
          }
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Login failed";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Something went wrong, try again later.");
    } finally {
      setShowLoader((prev) => ({ ...prev, getOtp: false }));
    }
  };

  const handleChangeMode = () => {
    setLoginState((prev) => ({
      ...prev,
      mode: prev.mode === "PASSWORD" ? "OTP" : "PASSWORD",
    }));

    setLoginInfo({
      identifier: { value: "", error: "" },
      password: { value: "", error: "" },
    });
  };

  // This function is used for onChange of OTP inputs
  const handleOtpChange = (value, index) => {
    if (!REGEX.ONLY_DIGITS_REGEX.test(value)) return;

    let newOtp = [...otpState.otp];
    newOtp[index] = value;
    setOtpState((prev) => ({ ...prev, otp: newOtp, error: "" }));

    if (value && index < 5) inputsRef.current[index + 1].focus();

    const finalOtp = newOtp.join("");
    if (finalOtp.length === 6 && !newOtp.includes("")) {
      setTimeout(() => {
        handleVerifyOtp(newOtp);
      }, 500); // auto verify
    }
  };

  // This function is used for enter key and backspace key functionality handling
  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Enter") {
      // const finalOtp = otpState.otp.join("");
      setTimeout(() => {
        handleVerifyOtp(otpState.otp);
      }, 500); // auto verify
      return;
    }
    if (e.key === "Backspace" && !otpState.otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Handle paste event to fill OTP boxes
  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").slice(0, 6);
    if (!REGEX.OTP_STRING_REGEX.test(text)) return;
    const digits = text.split("");
    setOtpState((prev) => ({
      ...prev,
      otp: prev.otp.map((_, i) => digits[i] || ""),
    }));
    if (digits.length === 6) {
      inputsRef.current[5].focus();
      setTimeout(() => {
        handleVerifyOtp(digits);
      }, 500); // auto verify
    }
  };

  // resend otp functionality
  const resendOtp = async () => {
    try {
      const bodyData = {
        uuid: loginState.otherData.uuid,
      };
      const res = await resendOTP(bodyData);
      if (res.code === 200) {
        setOtpState({
          ...otpState,
          otp: ["", "", "", "", "", ""],
          timer: OTP_TIMER,
          error: "",
        });
        inputsRef.current[0].focus();
        notify("success", res.message || "OTP has been resent successfully.");
        setLoginState((prev) => ({
          ...prev,
          otherData: { ...prev.otherData, ...res.data },
        }));
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to resend OTP, try again later.";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Something went wrong, try again later.");
    } finally {
    }
  };

  // verify OTP functionality, it's main function for step-2
  const handleVerifyOtp = async (otp, otherData = {}) => {
    const otpArray = Array.isArray(otp) ? otp : otpState.otp;
    const userOTP = otpArray.join("");

    if (userOTP.length === 6) {
      setShowLoader((prev) => ({ ...prev, verifyOtp: true }));
      try {
        const bodyData = {
          otp: userOTP,
          uuid: otherData.uuid || loginState.otherData.uuid,
          otpPurpose: "LOGIN",
          // otherData.otpPurpose || loginState.otherData.otpPurpose || "LOGIN",
        };

        // Use AuthContext verifyOtpLogin to handle OTP verify and login
        const result = await verifyOtpLogin(bodyData);

        if (result.success) {
          const role = result.data?.role; // CHANNEL_PARTNER //INSTRUCTOR //AADMIN

          const defaultRoute = ROLE_BASED_DEFAULT_ROUTE[role] || "/";
          notify("success", "OTP verified and Login successfully.");
          navigate(defaultRoute, { replace: true });
        } else {
          notify("danger", result.message);
          setOtpState((prev) => ({
            ...prev,
            error:
              result.message || "OTP verification failed, try again later.",
          }));
        }
      } catch (error) {
        notify("danger", "Something went wrong, try again later.");
      } finally {
        setShowLoader((prev) => ({ ...prev, verifyOtp: false }));
      }
    } else {
      const otpMessage = `Please enter the 6-digit OTP sent to your ${
        loginState.usedMethod === "email" ? "email" : "mobile number"
      }`;
      setOtpState((prev) => ({ ...prev, error: otpMessage }));
    }
  };

  // This function is used to navigate back to step 1 of registration
  const handleBack = () => {
    setOtpState((prev) => ({
      ...prev,
      isOtpMode: false,
      otp: ["", "", "", "", "", ""],
      timer: OTP_TIMER,
      error: "",
    }));
    setLoginInfo((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([key, field]) => [key, { ...field }]),
      ),
    );
  };

  return (
    <div className="login-page">
      <Container className="pb-4">
        <div className="header-body text-center">
          <Row className="justify-content-center">
            <Col lg="6" md="8" xl="5">
              <h1 className="text-white">Welcome!</h1>
              <p className="text-lead text-white mt-2">
                Raychem RPG's TMS - Training Management System
              </p>
            </Col>
          </Row>
        </div>
        <Row className="justify-content-center pt-2">
          <Col lg="6" md="9">
            <Card className="bg-grey shadow login-card">
              <CardHeader className="bg-transparent border-0 text-center pb-0">
                <div className="text-muted text-center mt-2 mb-2">
                  <small className="text-sm">Sign In with</small>
                </div>
                {/* <div className="btn-wrapper text-center">
                  <Button
                    className="btn-neutral btn-icon"
                    color="default"
                    onClick={(e) => e.preventDefault()}
                  >
                    <span className="btn-inner--icon mr-1">
                      <img
                        alt="..."
                        src={
                          require("assets/img/icons/common/google.svg").default
                        }
                      />
                    </span>
                    <span className="btn-inner--text text-deep-blue">
                      Google
                    </span>
                  </Button>
                  <Button
                    className="btn-neutral btn-icon"
                    color="default"
                    onClick={(e) => e.preventDefault()}
                  >
                    <span className="btn-inner--icon mr-1">
                      <img
                        alt="..."
                        src={
                          require("assets/img/icons/common/Microsoft_logo.svg")
                            .default
                        }
                      />
                    </span>
                    <span className="btn-inner--text text-deep-blue">
                      Microsoft
                    </span>
                  </Button>
                </div>
                <div className="divider">
                  <span>Or continue with credentials</span>
                </div> */}
              </CardHeader>

              <CardBody className="px-lg-5 py-lg-3">
                {!otpState.isOtpMode && (
                  <>
                    <Form role="form" onKeyDown={handleEnter}>
                      <FormGroup className={classnames("mb-3 pb-3")}>
                        <InputGroup className="input-group-merge input-group-alternative">
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="fa-solid fa-user"></i>
                            </InputGroupText>
                          </InputGroupAddon>
                          <Input
                            placeholder="Enter your email or mobile number"
                            name="identifier"
                            type="text"
                            value={loginInfo.identifier.value}
                            onKeyDown={preventSpace}
                            onChange={(e) =>
                              handleChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleBlur(e.target.name, e.target.value)
                            }
                            maxLength={
                              loginState.usedMethod === "mobile"
                                ? 10
                                : undefined
                            }
                            invalid={!!loginInfo.identifier.error}
                            autoComplete="new-identifier"
                          />
                        </InputGroup>
                        {loginInfo.identifier.error && (
                          <div className="invalid-feedback d-block">
                            {loginInfo.identifier.error}
                          </div>
                        )}
                      </FormGroup>

                      {loginState.mode !== "OTP" && (
                        <>
                          <FormGroup className={classnames("mb-3 pb-3")}>
                            <InputGroup className="input-group-merge input-group-alternative">
                              <InputGroupAddon addonType="prepend">
                                <InputGroupText>
                                  <i className="fa-solid fa-lock"></i>
                                </InputGroupText>
                              </InputGroupAddon>
                              <Input
                                placeholder="Enter your password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={loginInfo.password.value}
                                // onKeyDown={preventSpace}
                                onChange={(e) =>
                                  handleChange(e.target.name, e.target.value)
                                }
                                onBlur={(e) =>
                                  handleBlur(e.target.name, e.target.value)
                                }
                                autoComplete="new-password"
                              />
                              <InputGroupAddon addonType="append">
                                <InputGroupText
                                  onClick={() => setShowPassword(!showPassword)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <i
                                    className={
                                      showPassword
                                        ? "fa fa-eye"
                                        : "fa fa-eye-slash"
                                    }
                                  />
                                </InputGroupText>
                              </InputGroupAddon>
                            </InputGroup>
                            {loginInfo.password.error && (
                              <div className="invalid-feedback d-block">
                                {loginInfo.password.error}
                              </div>
                            )}
                          </FormGroup>
                          <span className="forget-pass">
                            <a href="#">Forgot password?</a>
                          </span>
                        </>
                      )}
                    </Form>

                    <Button
                      className="btn-vivid-red w-100 mt-2 py-3 text-md"
                      onClick={handleGetOtp}
                      disabled={
                        showLoader.getOtp ||
                        !!loginInfo.identifier.error ||
                        (loginState.mode === "PASSWORD" &&
                          !!loginInfo.password.error)
                      }
                    >
                      {showLoader.getOtp ? (
                        <i className="fa fa-spinner fa-spin fa-xl text-white" />
                      ) : loginState.mode === "PASSWORD" ? (
                        "Sign In"
                      ) : loginState.mode === "OTP" ? (
                        "Send OTP"
                      ) : (
                        ""
                      )}
                    </Button>

                    <div className="divider">
                      <span> Or </span>
                    </div>

                    <Button
                      className="btn-neutral w-100 mt-3 mb-4 py-3 text-md text-deep-blue"
                      color="default"
                      onClick={handleChangeMode}
                    >
                      Sign In with{" "}
                      {loginState.mode === "PASSWORD"
                        ? "OTP"
                        : loginState.mode === "OTP"
                          ? "Password"
                          : ""}
                    </Button>
                  </>
                )}

                {otpState.isOtpMode && (
                  <OTPScreen
                    otpState={otpState}
                    inputsRef={inputsRef}
                    handlePaste={handlePaste}
                    handleOtpChange={handleOtpChange}
                    handleOtpKeyDown={handleOtpKeyDown}
                    resendOtp={resendOtp}
                    enterdValue={loginInfo.identifier.value}
                    showLoader={showLoader}
                    verifyOtp={handleVerifyOtp}
                    showChangeIdentifier={true}
                    identifierLabel={loginState.usedMethod}
                    onChangeIdentifier={handleBack}
                    btnName="Verify & Sign In"
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
